import React, { useEffect, useMemo, useState } from "react";
import { getFilterConditionByField } from "../useFilterDetails";
import "./FilterChip.scss";
import { useFormContext, useWatch } from "react-hook-form";
import FilterBuilder from "../FilterBuilder/FilterBuilder";
import FilterBuilderProvider from "../FilterBuilder/FilterBuilderContext";
import { SeemPopover } from "@SeemplicityDev/shared";
import FilterChipButton from "./FilterChipButton";
import {
  FilterItem,
  FieldData,
  FilterPinItem,
} from "../../FilterManagement/CommonFilterTypes";
import type { FilterEntity } from "../../FilterManagement/filterEntity";
import {
  isActualStatusKey,
  isSameRemediationItemFilterKey,
} from "../../FilterManagement/filterKey";
import useRecentlyUsedFilters from "../useRecentlyUsedFilters";
import { filterTypes } from "../../../Findings/FindingsUtils";

export const clickAwayToIgnore = [
  "suggestions-name",
  "tags-suggestions",
  "react-datepicker",
];

export type FilterChipProps = {
  onFilterSelect: (filter: FilterItem | FilterItem[]) => void;
  filterItem?: FilterItem;
  namePrefix?: string;
  setMenuOpen?: (isOpen: boolean) => void;
  previewFilter?: () => void;
  itemIndex?: number;
  fieldsData: FieldData[];
  rowIndex?: number;
  removeFilter?: (index: number) => void;
  removeRow?: (index: number) => void;
  isEditable?: boolean;
  context: string;
  statusesData?: any[];
  shouldBlinkField?: string;
  resetBlinkField?: () => void;
  shouldOpenField?: string;
  resetOpenField?: () => void;
  allowRemove?: boolean;
  addValue?: (filter: FilterItem) => void;
  displayFullValue?: boolean;
  chipPopoverClassName?: string;
  filtersToPin?: FilterPinItem[];
  entity?: FilterEntity;
  isUnsupported?: boolean;
  addUsedField?: (field: string) => void;
  removeUsedField?: (field: string) => void;
};

const FilterChip: React.FC<FilterChipProps> = ({
  onFilterSelect,
  filterItem,
  previewFilter,
  itemIndex,
  fieldsData,
  rowIndex,
  removeFilter,
  removeRow,
  setMenuOpen,
  isEditable,
  context,
  statusesData = [],
  shouldBlinkField,
  resetBlinkField = () => {},
  shouldOpenField,
  resetOpenField = () => {},
  allowRemove = true,
  addValue = () => {},
  displayFullValue = false,
  chipPopoverClassName,
  filtersToPin = [],
  entity,
  addUsedField,
  removeUsedField,
  isUnsupported,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const { control } = useFormContext();
  const { saveNewFilterToLS } = useRecentlyUsedFilters({
    filterEntity: entity,
    filtersToPin,
  });

  const filterPanel = useWatch({
    control,
    name: `filterPanels`,
  });

  useEffect(() => {
    if (!filterItem?.field) {
      setIsPopoverOpen(true);
    } else {
      addUsedField?.(filterItem?.field);
    }
  }, [filterItem?.field]);

  useEffect(() => {
    setMenuOpen?.(isPopoverOpen);

    return () => {
      if (isPopoverOpen) {
        setMenuOpen?.(false);
      }
    };
  }, [isPopoverOpen]);

  useEffect(() => {
    if (shouldOpenField && shouldOpenField === filterItem?.field) {
      setIsPopoverOpen(true);
      resetOpenField();
    }
  }, [shouldOpenField, filterItem?.field, resetOpenField]);

  const handleFilterSelect = (filter: FilterItem | FilterItem[]) => {
    if (Array.isArray(filter) && !!filter.length) {
      filter?.forEach(({ field, values }: any, index: number) => {
        const condition = getFilterConditionByField(field, fieldsData);
        if (!condition) {
          return;
        }

        if (index === 0) {
          onFilterSelect({ field, condition, value: values });
        } else {
          addValue({
            field,
            condition,
            value: values,
          });
        }
      });
    } else {
      onFilterSelect(filter);
      if (
        context === filterTypes.FILTER &&
        !filtersToPin?.find((f: FilterPinItem) =>
          isSameRemediationItemFilterKey(
            f.filterField,
            (filter as FilterItem)?.field
          )
        )
      ) {
        saveNewFilterToLS(filter as FilterItem);
      }
    }

    setIsPopoverOpen(false);
  };

  const handleRemoveFilter = () => {
    if (itemIndex !== undefined && removeFilter) {
      removeFilter(itemIndex);
    }

    setTimeout(() => {
      if (
        rowIndex !== undefined &&
        itemIndex === 0 &&
        filterPanel?.[rowIndex]?.conditions?.length === 1 &&
        filterPanel?.length > 1
      ) {
        removeRow?.(rowIndex);
      }
    }, 0);

    setTimeout(() => {
      previewFilter?.();
    }, 10);

    if (!!filterItem?.field) {
      removeUsedField?.(filterItem?.field);
    }
  };

  const handleCancel = () => {
    if (!filterItem?.field) {
      handleRemoveFilter();
    }
    setIsPopoverOpen(false);
  };

  const clickAway = (e: Event) => {
    const disallowedPaths = e
      ?.composedPath()
      ?.filter((path) =>
        clickAwayToIgnore.some((className) =>
          (path as Element)?.className?.includes(className)
        )
      );

    if (!disallowedPaths?.length) {
      handleCancel();
    }
  };

  const shouldBlink = useMemo(() => {
    const blink = shouldBlinkField;
    const isBlinking =
      !!blink && !!filterItem?.field && blink === filterItem.field;
    if (isBlinking) {
      resetBlinkField();
    }

    return isBlinking;
  }, [shouldBlinkField, filterItem]);

  return (
    <SeemPopover
      triggerClassName={"filter-chip-wrap"}
      isOpen={isPopoverOpen}
      // SeemPopover's onOuterAction is typed `() => void` and is invoked with no
      // arguments at runtime (setTimeout(onOuterAction, 200)), so `clickAway`'s
      // event param is always undefined here. The body already guards every
      // access with `?.`, so behavior is preserved; cast to bridge the signature.
      onOuterAction={clickAway as () => void}
      placement={"bottom"}
      body={
        <div
          className={`chip-popover-wrap ${chipPopoverClassName || ""} ${isActualStatusKey(filterItem?.field) ? "narrow-popover" : ""}`}
          data-testid={`${context.toLowerCase()}-popover-wrap`}
        >
          <FilterBuilderProvider
            onFilterSelect={handleFilterSelect}
            onCancel={handleCancel}
            currentFilter={filterItem}
            fieldsData={fieldsData}
            context={context}
            statusesData={statusesData}
            filtersToPin={filtersToPin}
            entity={entity}
          >
            <FilterBuilder />
          </FilterBuilderProvider>
        </div>
      }
    >
      <FilterChipButton
        filterItem={filterItem}
        isActive={isPopoverOpen}
        isBlink={shouldBlink}
        isUnsupported={isUnsupported}
        onClick={() => setIsPopoverOpen((prevState) => !prevState)}
        displayFullValue={displayFullValue}
        allowRemove={allowRemove}
        handleRemoveFilter={handleRemoveFilter}
        isEditable={isEditable}
        fieldsData={fieldsData}
        statusesData={statusesData}
        filtersToPin={filtersToPin}
      />
    </SeemPopover>
  );
};

export default FilterChip;
